import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ThemeRegistry } from '@classy/theme-sdk';
import { useApi } from '../../api/ApiProvider.js';
import type { MeDto } from '../../api/types.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { SessionBg } from '../../theme/SessionBg.js';
import { ClassroomBg } from '../../theme/ClassroomBg.js';
import { ClassroomLandingBg } from '../../theme/ClassroomLandingBg.js';
import { publicThemeKeys, personalThemeKeys } from '../../theme/themeAccess.js';
import { useAuth } from '../../auth/AuthContext.js';
import { KioskStage } from '../../components/KioskStage.js';
import { HubShell } from './HubShell.js';
import { ClassroomDetail } from '../Classrooms/ClassroomDetail.js';
import { LiveSurface } from './LiveSurface.js';

export interface PhaseRouterProps {
  registry: ThemeRegistry;
}

/**
 * Top-level post-auth router. Picks one of three phases and wraps each
 * in the right theme + background:
 *
 *   1. Hub          — no active or selected classroom; SessionBg lays
 *                     the fixed calm auth bg behind the greeting.
 *   2. ClassroomDetail (the "classroom landing page") — a classroom is
 *                     selected for configuration; ClassroomLandingBg
 *                     keeps the same calm auth bg but wraps the subtree
 *                     in the classroom's ThemeProvider so ThemePicker +
 *                     mascot previews still resolve against its theme.
 *   3. Live           — a classroom is active (running morning meeting);
 *                     ClassroomBg swaps in the classroom's themed bg.
 *
 * `selectedClassroomId` is held here (one level above HubShell) so it
 * survives the user activating from a card and pressing back from
 * live mode — they land on the classroom's detail page, not the hub
 * list. Detail's back button clears this; only then does the user
 * drop to the hub list.
 */
export function PhaseRouter({ registry }: PhaseRouterProps) {
  const api = useApi();
  const auth = useAuth();
  const { active } = useActiveClassroom();
  const [selectedClassroomId, setSelectedClassroomId] = useState<string | null>(null);

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.client.get<MeDto>('/api/me'),
    retry: false,
  });

  const signInEmail = auth.account?.email;
  const allowedThemeKeys = useMemo(() => {
    const base = meQuery.data?.organization.enabledThemeKeys ?? publicThemeKeys(registry);
    // Unlock any private themes tied to this teacher's sign-in email (e.g.
    // sarah) on top of the org allowlist.
    const personal = personalThemeKeys(signInEmail);
    return personal.length ? [...new Set([...base, ...personal])] : base;
  }, [meQuery.data?.organization.enabledThemeKeys, signInEmail, registry]);

  // Pull every classroom (active + archived) so detail-by-id works
  // regardless of the hub tab currently shown.
  const classroomsQuery = useQuery({
    queryKey: ['classrooms', 'all'],
    queryFn: async () => {
      const [activeList, archivedList] = await Promise.all([
        api.classrooms.list('active'),
        api.classrooms.list('archived'),
      ]);
      return [...activeList, ...archivedList];
    },
  });

  const selectedClassroom = useMemo(() => {
    if (!selectedClassroomId) return null;
    return classroomsQuery.data?.find((c) => c.id === selectedClassroomId) ?? null;
  }, [selectedClassroomId, classroomsQuery.data]);

  // Phase 3 — live morning meeting
  if (active) {
    return (
      <ClassroomBg registry={registry} classroomId={active.id} allowedThemeKeys={allowedThemeKeys}>
        <KioskStage live>
          <LiveSurface />
        </KioskStage>
      </ClassroomBg>
    );
  }

  // Phase 2 — classroom-detail configuration (the "classroom landing page")
  if (selectedClassroom) {
    return (
      <ClassroomLandingBg
        registry={registry}
        classroomId={selectedClassroom.id}
        allowedThemeKeys={allowedThemeKeys}
      >
        <KioskStage scroll>
          <ClassroomDetail
            classroom={selectedClassroom}
            onBack={() => setSelectedClassroomId(null)}
          />
        </KioskStage>
      </ClassroomLandingBg>
    );
  }

  // Phase 1 — hub
  return (
    <SessionBg registry={registry} allowedThemeKeys={allowedThemeKeys}>
      <KioskStage>
        <HubShell onSelectClassroom={setSelectedClassroomId} />
      </KioskStage>
    </SessionBg>
  );
}
